/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { formSelectors } from '../../../../app/page/page-storage/users/user-form/user-form.selectors';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import { IUser } from '../../../../app/resource/user/user.types';
import { convertDateAndTimeFromUTC } from '../../../../utils/date.utils';
import { useRequiredValidation } from '../../../../utils/form/validation.utils';
import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { AvatarInput } from '../../common/inputs/AvatarInput';
import { ColorInput } from '../../common/inputs/ColorInput';
import { DateTimeInput } from '../../common/inputs/DateTimeInput';
import { SelectInput } from '../../common/inputs/SelectInput';
import { FormTextInput } from '../../common/inputs/TextInput';
import { IMeetingFormProps, IntervalConfig, IntervalStrategy } from './types';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import { settingsSelectors } from '../../../../app/resource/settings/settings.selectors';
import { subscriptionAccessSelectors } from '../../../../app/resource/subscription-access/subscription-access.selectors';
import { generateTimeOptions } from '../../../../utils/form/startTimeOptions.util';
import {
  IMeetingSimpleUpdate,
  IMeetingSimpleUpdateForm,
} from '../../../../app/resource/meeting-simple/meeting-simple.types';

function preSubmit(data: IMeetingSimpleUpdateForm): IMeetingSimpleUpdate {
  return {
    ...data,
    repeatType: data.repeatType ? data.repeatType.value : null,
    startTime: data.startTime.value,
    administrators: data.administrators.map((tuple) => tuple.value),
    shareWith: data.shareWith.map((tuple) => tuple.value),
    precapMessage: data.precapMessage && data.precapMessage.trim() !== '' ? data.precapMessage : null,
  };
}

export const MeetingForm: FunctionComponent<IMeetingFormProps> = ({ type, onSubmit, data, errorCheck }) => {
  const formProps = useForm();
  const [isCustomInterval, setIsCustomInterval] = useState(false);
  const [interval, setInterval] = useState<{ value: string; label: string }>();
  const quillRef = useRef<ReactQuill | null>(null);

  const loggedUserId = useSelect(authSelectors.selectLoggedUser) || '';
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId);
  const settings = useSelectWithParams(
    settingsSelectors.selectResourceById,
    loggedUser?.tenantId ? loggedUser.tenantId : '',
  );
  const aiNotesEnabled = useSelect(subscriptionAccessSelectors.selectAiNotesEnabled);

  const requiredValidation = useRequiredValidation();

  const imageData = useSelect(formSelectors.selectFormAvatar) as string;
  const cropCounter = useSelect(formSelectors.selectCropCounter) as number;

  const currentDate = useMemo(() => {
    const startDate = formProps.getValues().startDate;
    return {
      dayOfWeek: moment(startDate).format('dddd'),
      dayOfMonth: moment(startDate).date(),
      monthAndDay: moment(startDate).format('MMMM Do'),
    };
  }, [formProps]);

  // Error displaying
  useEffect(() => {
    const error = formProps.errors;

    if (errorCheck === true) {
      const errors = [
        error.color?.ref.name ? 'Meeting color' : undefined,
        error.title?.ref.name ? 'Meeting title' : undefined,
        error.administrators?.ref.name ? 'Meeting leaders' : undefined,
        error.shareWith?.ref.name ? 'Meeting members' : undefined,
        error.sharepointUrl?.ref.name ? 'Meeting URL' : undefined,
        error.startTime?.ref.name ? 'Start time' : undefined,
      ];
      if (imageData !== '' && cropCounter > 1) {
        errors[0] = undefined;
      }

      for (let i = 0; i < errors.length; i++) {
        if (errors[i] !== undefined) {
          alert(`${errors[i]} field is required.`);
          break;
        }
      }
    }
  }, [formProps.errors, errorCheck, cropCounter, imageData]);

  //#region Users / Members

  const users = useSelect(userSelectors.selectResourceList);
  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })),
    [users],
  );
  const administrators = useSelectWithParams(
    userSelectors.selectResourcesById,
    data ? data.administratorIds : [],
  ) as (IUser | null)[];
  const mappedAdministrators = useMemo(
    () =>
      administrators
        .filter((user): user is IUser => user !== null)
        .map((user) => ({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`,
        })),
    [administrators],
  );

  const administratorDefaultOptions = useMemo(() => (data ? mappedAdministrators : undefined), [
    data,
    mappedAdministrators,
  ]);

  const shareWith = useSelectWithParams(
    userSelectors.selectResourcesById,
    data ? data.shareWithIds : [],
  ) as (IUser | null)[];

  const mappedShareWith = useMemo(
    () =>
      shareWith
        .filter((user): user is IUser => user !== null)
        .map((user) => ({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`,
        })),
    [shareWith],
  );

  const shareWithDefaultOptions = useMemo(() => (data ? mappedShareWith : undefined), [data, mappedShareWith]);

  //#endregion Users / Members

  //#region Date and Time select and options

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  let defaultStartDateValue: string | undefined;
  let defaultStartTimeValue: string | undefined;
  if (data?.startDate && data?.startTime && loggedUser?.timezone) {
    ({ date: defaultStartDateValue, time: defaultStartTimeValue } = convertDateAndTimeFromUTC(
      data?.startDate,
      data?.startTime,
      loggedUser?.timezone,
      { timeLeadingZeo: false }, // to use the same format as the time options
    ));
  }

  const startDate = useMemo(() => {
    try {
      if (defaultStartDateValue) {
        const dateString = moment(defaultStartDateValue).format('YYYY-MM-DD HH:mm');
        return new Date(dateString);
      } else {
        return new Date();
      }
    } catch (error) {
      console.error('Calculating start date value', error);
    }
  }, [defaultStartDateValue]);

  const defaultStartTimeOption = useMemo(() => {
    const defaultTimeOption = timeOptions.find((option) => defaultStartTimeValue === option.value);
    if (defaultTimeOption) {
      return defaultTimeOption;
    } else {
      return timeOptions[0];
    }
  }, [defaultStartTimeValue, timeOptions]);

  // Converting back to UTC in order to send UTC time to the api
  useEffect(() => {
    const formData = formProps.getValues();

    const startValueInUTC = moment(formData.startTime.value, 'HH:mm').utc().format('HH:mm');
    const startLabelInUTC = moment(formData.startTime.label, 'hh:mm A').utc().format('HH:mm');

    formData.startTime = { value: startValueInUTC, label: startLabelInUTC };
  }, [formProps, loggedUser]);

  //#endregion Date and Time select and options

  //#region Repeat and intervals

  const repeatOptions = [
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'years', label: 'Years' },
  ];

  const intervalOptions = [
    { value: 'doNotRepeat', label: 'Do not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: `Weekly on XXXXX` },
    { value: 'monthly', label: `Monthly on the XXXXX` },
    { value: 'annually', label: `Annually on XXXXX` },
    { value: 'custom', label: 'Custom...' },
  ];

  const intervalStrategy: IntervalStrategy = {
    daily: { repeatType: { value: 'days', label: 'Days' }, repeatEvery: 1 },
    weekly: { repeatType: { value: 'weeks', label: 'Weeks' }, repeatEvery: 1 },
    monthly: { repeatType: { value: 'months', label: 'Months' }, repeatEvery: 1 },
    annually: { repeatType: { value: 'years', label: 'Years' }, repeatEvery: 1 },
  };

  const handleGetOptionLabel = (option?: { value: string; label: string }) => {
    switch (option?.value) {
      case 'weekly':
        return `Weekly on ${currentDate.dayOfWeek}`;
      case 'monthly':
        return `Monthly on the ${currentDate.dayOfMonth}`;
      case 'annually':
        return `Annually on ${currentDate.monthAndDay}`;
      default:
        return option?.label ?? '';
    }
  };

  const calculateRepeatBasedOnInverval = useCallback(() => {
    if (interval) {
      setIsCustomInterval(interval.value === 'custom');
      if (interval.value === 'custom') {
        // is not custom
        // Nothing specific to do
      } else {
        // Is custom

        if (interval.value === 'doNotRepeat') {
          // cleanup the repeatType
          formProps.setValue('repeatType', null);
        } else {
          const { repeatEvery, repeatType }: IntervalConfig = intervalStrategy[interval.value];
          formProps.setValue('repeatEvery', repeatEvery);
          formProps.setValue('repeatType', repeatType);
        }
      }
    } else {
      // default is Weekly
      formProps.setValue('repeatEvery', 1);
      formProps.setValue('repeatType', { value: 'weeks', label: 'Weeks' });
    }
  }, [formProps, interval, intervalStrategy]);

  useEffect(() => {
    setInterval(formProps.watch('interval'));
    calculateRepeatBasedOnInverval();
  }, [calculateRepeatBasedOnInverval, data, formProps, interval, isCustomInterval]);

  const defaultIntervalOption = useMemo(() => {
    // check whether is editing or not
    if (data) {
      if (data.repeatType == null) {
        return intervalOptions[0]; // do not repeat
      }
      // see if an strategy match
      const strategyKey = Object.keys(intervalStrategy).find(
        (strategyKey) =>
          intervalStrategy[strategyKey].repeatType.value === data.repeatType &&
          String(intervalStrategy[strategyKey].repeatEvery) === String(data.repeatEvery),
      );
      if (strategyKey) {
        // now find which interval corresponds
        const intervalForStrategy = intervalOptions.find((interval) => interval.value === strategyKey);
        return intervalForStrategy;
      }
      return intervalOptions[5]; // custom
    } else {
      // is new (not edit), default to weekly
      return intervalOptions[2]; // weekly
    }
  }, [data, intervalOptions, intervalStrategy]);

  //#endregion Repeat and intervals

  const defaultURL = data?.sharepointUrl
    ? data?.sharepointUrl &&
      (data?.sharepointUrl.slice(0, 8) === 'https://' || data?.sharepointUrl.slice(0, 7) === 'http://')
      ? data.sharepointUrl
      : `https://${data?.sharepointUrl}`
    : '';

  return (
    <FormProvider {...formProps}>
      <form
        id="meeting-form"
        className="topic-form"
        onSubmit={formProps.handleSubmit((formData) => onSubmit(preSubmit(formData as IMeetingSimpleUpdateForm)))}
        action=""
        method={!!data ? 'put' : 'post'}
      >
        <div className="popup-upload-panel">
          <ColorInput
            id="color"
            name="color"
            placeholder={`Pick ${type === 'account' ? 'Account' : 'Meeting'} Color *`}
            description="Meeting initials will be automatically generated from account name."
            validation={requiredValidation}
            defaultValue={data?.color}
          />
        </div>
        <div className="row">
          <div className="col-12" style={{ marginTop: '20px' }}>
            <div className="field-container">
              <FormTextInput
                id="title"
                name="title"
                type="text"
                label={type === 'account' ? 'Account Title *' : 'Meeting Name *'}
                placeholder="Enter Title"
                validation={requiredValidation}
                defaultValue={data?.title}
              />
            </div>
          </div>
          <div className="col-12">
            <div className="field-container">
              <SelectInput
                id="administrators"
                name="administrators"
                label={type === 'account' ? 'Account Leaders *' : 'Meeting Leaders *'}
                placeholder="Search..."
                validation={requiredValidation}
                options={userOptions}
                defaultOptions={administratorDefaultOptions}
                clearAllActionAvailable={false}
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field-container">
              <SelectInput
                id="shareWith"
                name="shareWith"
                label={type === 'account' ? 'Account Members *' : 'Meeting Members *'}
                placeholder="Search..."
                validation={requiredValidation}
                options={userOptions}
                defaultOptions={shareWithDefaultOptions}
                clearAllActionAvailable={false}
              />
            </div>
          </div>
        </div>
        <h4>Meeting</h4>
        <div className="row">
          <div className="col-12">
            <div className="field-container">
              <FormTextInput
                id="sharepointUrl"
                name="sharepointUrl"
                type="text"
                label="Meeting URL (ie - Zoom, Teams, Meet, etc.)"
                placeholder="Enter Meeting URL"
                defaultValue={defaultURL}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="field-container">
              <DateTimeInput
                id="startDate"
                name="startDate"
                label="First meeting date"
                defaultValue={startDate}
                placeholder="Enter Start Date"
                validation={requiredValidation}
              />
            </div>
          </div>
        </div>
        <div className="col-10">
          <div className="row">
            <div className="col-6">
              <div className="field-container">
                <SelectInput
                  id="startTime"
                  name="startTime"
                  label="Start Time"
                  placeholder="Enter Start Time"
                  defaultOptions={defaultStartTimeOption}
                  options={timeOptions}
                  validation={requiredValidation}
                  isSingle
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-10">
          <div className="row">
            <div className="col-6">
              <div className="field-container">
                <div className="field-container" style={{ marginTop: '18px' }}>
                  <SelectInput
                    id="interval"
                    name="interval"
                    label="Repeat"
                    placeholder="Select interval..."
                    validation={requiredValidation}
                    options={intervalOptions}
                    defaultOptions={defaultIntervalOption}
                    isSingle
                    onGetOptionLabel={handleGetOptionLabel}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={isCustomInterval ? {} : { display: 'none' }}>
          <div className="row" id="repeat-segment">
            <div className="col-6">
              <div className="field-container">
                <FormTextInput
                  id="repeatEvery"
                  name="repeatEvery"
                  type="text"
                  label="Repeat Every"
                  placeholder="Enter"
                  defaultValue={data?.repeatEvery ? data?.repeatEvery : '1'}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="field-container" style={{ marginTop: '18px' }}>
                <SelectInput
                  id="repeatType"
                  name="repeatType"
                  validation={isCustomInterval ? requiredValidation : undefined}
                  options={repeatOptions}
                  defaultOptions={
                    data?.repeatType ? { value: data.repeatType, label: data.repeatType } : repeatOptions[1]
                  }
                  isSingle
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="field-container precap-message-editor">
              <label htmlFor="precapMessage">Precap Message</label>
              <Controller
                name="precapMessage"
                control={formProps.control}
                defaultValue={data?.precapMessage ?? ''}
                render={({ onChange, value }) => (
                  <>
                    <ReactQuill
                      theme="snow"
                      value={value || ''}
                      onChange={onChange}
                      placeholder="Enter precap message for the meeting..."
                      ref={(el) => {
                        quillRef.current = el;
                      }}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <div className="f-1 mt-2" style={{ display: 'block', marginBottom: '4px', color: '#66717c' }}>
                        Placeholders
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn darkGray f-1 gray-bg ml-0 mt-1 px-1"
                          onClick={() => {
                            if (quillRef.current) {
                              const editor = quillRef.current.getEditor();
                              editor.insertText(editor.getSelection()?.index || 0, '{{ USER_NAME }}');
                              onChange(editor.root.innerHTML);
                            }
                          }}
                        >
                          User Name
                        </button>
                      </div>
                    </div>
                  </>
                )}
              />
            </div>
          </div>
        </div>
        {settings?.enableMeetingsRecording && aiNotesEnabled && (
          <div className="popup-upload-panel row mb-1">
            <div className="col-12 pr-1">
              <div className="flex aic">
                <label className="switch big-switch mr-1 mb-0" htmlFor="enableRecording">
                  <input
                    type="checkbox"
                    className="switch-input change-theme"
                    id="enableRecording"
                    name="enableRecording"
                    defaultChecked={!!data?.enableRecording}
                    ref={formProps.register}
                  />
                  <span className="switch-label"></span>
                  <span className="switch-handle"></span>
                </label>
                <span className="switch-inline-text">Enable meeting recording and transcription</span>
              </div>
            </div>
          </div>
        )}
        <div className="popup-upload-panel">
          {/* <ImageInput
            id="image"
            name="image"
            label={`Upload ${type === 'account' ? 'Account' : 'Meeting'}  Photo`}
            maxImageSize={2}
            acceptableFormats={'jpg, png, gif'}
            validation={requiredValidation}
            defaultValue={data?.image}
          /> */}
          <AvatarInput
            id="image"
            name="image"
            label={`Upload ${type === 'account' ? 'Account' : 'Meeting'}  Photo`}
            maxImageSize={2}
            acceptableFormats={'jpg, png, gif'}
            defaultValue={data?.image}
          />
        </div>
      </form>
    </FormProvider>
  );
};
